import React from 'react';
import { Drawer, Empty, List, Space, Tag, Typography } from 'antd';
import { ExportOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import {
    DemoItem,
    DocItem,
    EventType,
    EVENT_TYPE_LABELS,
    IssueItem,
    MemberEventItem,
    MergedPrItem,
    PrItem,
    TeamMemberInsight,
} from './types';

export type DrillDownMetric =
    | 'prsMerged'
    | 'prsActedOn'
    | 'issuesOpened'
    | 'issuesClosed'
    | 'issuesActedOn'
    | 'docsCreated'
    | 'demos'
    | 'events';

interface Props {
    open: boolean;
    onClose: () => void;
    member: TeamMemberInsight | null;
    metric: DrillDownMetric | null;
    /** When the metric is "events", optionally narrows the list to a single event type. */
    eventType?: EventType | null;
    window: { from: string; to: string };
}

const METRIC_LABEL: Record<DrillDownMetric, string> = {
    prsMerged: 'PRs merged',
    prsActedOn: 'PRs acted on',
    issuesOpened: 'Issues opened',
    issuesClosed: 'Issues closed',
    issuesActedOn: 'Issues acted on',
    docsCreated: 'Docs created',
    demos: 'Demos & presentations',
    events: 'Events',
};

const fmtDate = (iso?: string | null): string =>
    iso ? dayjs(iso).format('YYYY-MM-DD HH:mm') : '—';

const ItemLink: React.FC<{ url: string; children: React.ReactNode }> = ({ url, children }) => (
    <a href={url} target="_blank" rel="noopener noreferrer">
        {children} <ExportOutlined style={{ fontSize: 12 }} />
    </a>
);

const renderMergedPr = (it: MergedPrItem) => (
    <List.Item key={it.ref}>
        <List.Item.Meta
            title={<ItemLink url={it.url}>{it.title}</ItemLink>}
            description={
                <span>
                    <Typography.Text type="secondary">{it.ref}</Typography.Text> · merged{' '}
                    {fmtDate(it.mergedAt)} ·{' '}
                    <Tag color="green">+{it.additions.toLocaleString()}</Tag>
                    <Tag color="red">−{it.deletions.toLocaleString()}</Tag>
                </span>
            }
        />
    </List.Item>
);

const renderPr = (it: PrItem) => (
    <List.Item key={it.ref}>
        <List.Item.Meta
            title={<ItemLink url={it.url}>{it.title}</ItemLink>}
            description={<Typography.Text type="secondary">{it.ref}</Typography.Text>}
        />
    </List.Item>
);

const renderIssue = (label: 'opened' | 'closed' | 'acted on') => (it: IssueItem) => {
    const date = label === 'opened' ? it.createdAt : label === 'closed' ? it.closedAt : undefined;
    return (
        <List.Item key={it.ref}>
            <List.Item.Meta
                title={<ItemLink url={it.url}>{it.title}</ItemLink>}
                description={
                    <span>
                        <Typography.Text type="secondary">{it.ref}</Typography.Text>
                        {date && (
                            <>
                                {' '}· {label} {fmtDate(date)}
                            </>
                        )}
                    </span>
                }
            />
        </List.Item>
    );
};

const renderDoc = (it: DocItem) => (
    <List.Item key={it.id}>
        <List.Item.Meta
            title={<ItemLink url={it.url}>{it.name}</ItemLink>}
            description={
                <Typography.Text type="secondary">
                    created {fmtDate(it.createdAt)}
                </Typography.Text>
            }
        />
    </List.Item>
);

const renderDemo = (it: DemoItem) => (
    <List.Item key={it.id}>
        <List.Item.Meta
            title={
                it.url ? <ItemLink url={it.url}>{it.title}</ItemLink> : <span>{it.title}</span>
            }
            description={<Typography.Text type="secondary">{it.date}</Typography.Text>}
        />
    </List.Item>
);

const renderEvent = (it: MemberEventItem) => {
    const label = EVENT_TYPE_LABELS[it.type as EventType] ?? it.type;
    return (
        <List.Item key={it.id}>
            <List.Item.Meta
                title={
                    <Space>
                        <Typography.Text strong>{it.date}</Typography.Text>
                        <Tag>{label}</Tag>
                    </Space>
                }
                description={
                    it.note ? (
                        <Typography.Text>{it.note}</Typography.Text>
                    ) : (
                        <Typography.Text type="secondary">No note</Typography.Text>
                    )
                }
            />
        </List.Item>
    );
};

const DrillDownDrawer: React.FC<Props> = ({ open, onClose, member, metric, eventType, window }) => {
    if (!member || !metric) {
        return <Drawer open={open} onClose={onClose} width={520} />;
    }
    const label =
        metric === 'events' && eventType
            ? EVENT_TYPE_LABELS[eventType] ?? METRIC_LABEL[metric]
            : METRIC_LABEL[metric];

    let body: React.ReactNode;
    switch (metric) {
        case 'prsMerged':
            body = (
                <List
                    dataSource={member.prsMergedItems}
                    renderItem={renderMergedPr}
                    locale={{ emptyText: <Empty description="No PRs in this window" /> }}
                />
            );
            break;
        case 'prsActedOn':
            body = (
                <List
                    dataSource={member.prsActedOnItems}
                    renderItem={renderPr}
                    locale={{ emptyText: <Empty description="No PRs acted on in this window" /> }}
                />
            );
            break;
        case 'issuesOpened':
            body = (
                <List
                    dataSource={member.issuesOpenedItems}
                    renderItem={renderIssue('opened')}
                    locale={{ emptyText: <Empty description="No issues opened in this window" /> }}
                />
            );
            break;
        case 'issuesClosed':
            body = (
                <List
                    dataSource={member.issuesClosedItems}
                    renderItem={renderIssue('closed')}
                    locale={{ emptyText: <Empty description="No issues closed in this window" /> }}
                />
            );
            break;
        case 'issuesActedOn':
            body = (
                <List
                    dataSource={member.issuesActedOnItems}
                    renderItem={renderIssue('acted on')}
                    locale={{ emptyText: <Empty description="No issues acted on in this window" /> }}
                />
            );
            break;
        case 'docsCreated':
            body = (
                <List
                    dataSource={member.docsCreatedItems}
                    renderItem={renderDoc}
                    locale={{ emptyText: <Empty description="No docs created in this window" /> }}
                />
            );
            break;
        case 'demos':
            body = (
                <List
                    dataSource={member.demosItems}
                    renderItem={renderDemo}
                    locale={{ emptyText: <Empty description="No demos in this window" /> }}
                />
            );
            break;
        case 'events': {
            const items = eventType
                ? member.eventsItems.filter((it) => it.type === eventType)
                : member.eventsItems;
            body = (
                <List
                    dataSource={items}
                    renderItem={renderEvent}
                    locale={{ emptyText: <Empty description="No events in this window" /> }}
                />
            );
            break;
        }
    }

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={520}
            title={
                <div>
                    <Typography.Text strong>{member.login}</Typography.Text>{' '}
                    <Typography.Text type="secondary">· {label}</Typography.Text>
                </div>
            }
            extra={
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    {dayjs(window.from).format('YYYY-MM-DD')} → {dayjs(window.to).format('YYYY-MM-DD')}
                </Typography.Text>
            }
        >
            {body}
        </Drawer>
    );
};

export default DrillDownDrawer;
